import { Component, OnInit, ViewChild } from "@angular/core";
import { FormGroup, FormArray, FormBuilder, Validators, ReactiveFormsModule } from "@angular/forms";
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RheologyExpectedSoComponent } from '../graph/rheology-expected-so/rheology-expected-so.component';
import { Rheology100SoComponent } from '../graph/rheology100-so/rheology100-so.component';
import { ReynoldsNumberComponent } from '../graph/reynolds-number/reynolds-number.component';
import { FlowCurvesComponent } from '../graph/flow-curves/flow-curves.component';
import { SoResults, Fluid, SharedSettingsService, FluidReading, RheologyService } from '../../services/rheological.service';

@Component({
  selector: 'app-hierarchy',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RheologyExpectedSoComponent,
    Rheology100SoComponent,
    ReynoldsNumberComponent,
    FlowCurvesComponent
  ],
  templateUrl: './hierarchy.component.html',
  styleUrls: ['./hierarchy.component.css'],
})
export class HierarchyComponent {
  fluidForm: FormGroup;
  standOffOverride: number | null = 100;
  rheologyOverride: number | null = 20;

  // Results after calculate
  soResults: SoResults | null = null;
  calculatedFluids: Fluid[] = [];
  showGraphs = false;

  constructor(private fb: FormBuilder, private rheologyService: RheologyService, private sharedSettingsService: SharedSettingsService) {
    this.fluidForm = this.fb.group({
      fluids: this.fb.array([]),
    });
  }

  hasValue(value: number | null): boolean {
    return value !== null && value !== undefined && value !== 0 && value.toString() !== '';
  }

  // Add a new fluid card
  addFluid(): void {
    const fluidCount = this.fluids().length;
    let defaultName = '';
    
    // Set default names for first three fluids
    if (fluidCount === 0) {
      defaultName = 'Mud';
    } else if (fluidCount === 1) {
      defaultName = 'Spacer';
    } else if (fluidCount === 2) {
      defaultName = 'Slurry';
    }
    
    const fluidGroup = this.fb.group({
      name: [defaultName],
      density: [''],
      readingCounts: [5],
      readings: this.fb.array([
        this.fb.group({ rpm: [300], value: [''] }),
        this.fb.group({ rpm: [200], value: [''] }),
        this.fb.group({ rpm: [100], value: [''] }),
        this.fb.group({ rpm: [6], value: [''] }),
        this.fb.group({ rpm: [3], value: [''] })
      ])
    });
    this.fluids().push(fluidGroup);
  }

  // Remove a fluid card
  removeFluid(index: number): void {
    this.fluids().removeAt(index);
  }

  // Form Array Getters
  fluids(): FormArray {
    return this.fluidForm.get("fluids") as FormArray;
  }

  fluidReadings(fldIndex: number): FormArray {
    return this.fluids().at(fldIndex).get("readings") as FormArray;
  }

  addFluidReadings(fldIndex: number, rpm: number): void {
    this.fluidReadings(fldIndex).push(
      this.fb.group({ rpm: [rpm], value: [''] })
    );
  }

  removeFluidReadings(fldIndex: number, readingIndex: number): void {
    this.fluidReadings(fldIndex).removeAt(readingIndex);
  }

  get isCalculateEnabled(): boolean {
    if (this.fluids().length === 0) return false;
    return this.fluids().controls.every(fluid => {
      const density = fluid.get('density')?.value;
      if (density === null || density === undefined || density === '') return false;

      const readings = (fluid.get('readings') as FormArray).controls;
      return readings.every(r => {
        const val = r.get('value')?.value;
        return val !== null && val !== undefined && val !== '';
      });
    });
  }

  readingCountChange(event: Event, fldIndex: number): void {
    let selectedCount = (event.target as HTMLInputElement)
      .value as unknown as number;
    let readings = this.rheologyService.getReadings(selectedCount);

    var arrayLength = this.fluidReadings(fldIndex).controls.length;

    for (let i = arrayLength - 1; i >= 0; i--) {
      this.removeFluidReadings(fldIndex, i);
    }

    for (var reading of readings) {
      this.addFluidReadings(fldIndex, reading);
    }
  }

  get fluidTitlesList(): string[] {
    return this.calculatedFluids.map(f => f.title);
  }

  calculate(): void {
    // Sync overrides into shared settings
    this.sharedSettingsService.setSharedSetting({
      standOffOverride: this.standOffOverride,
      rheologicalOverride: this.rheologyOverride
    });

    // Build Fluid objects from the form
    this.calculatedFluids = this.fluids().controls.map(fluidCtrl => {
      const readings: FluidReading[] = (fluidCtrl.get('readings') as FormArray).controls.map(r => ({
        rpm: +r.get('rpm')!.value,
        rmpVal: +r.get('value')!.value
      }));

      const fluid: Fluid = {
        title: fluidCtrl.get('name')!.value,
        density: +fluidCtrl.get('density')!.value,
        readingsCount: readings.length,
        readings
      };

      this.rheologyService.setFluidEquationCoeffs(fluid);
      this.rheologyService.calculateFluidRheology(fluid);
      return fluid;
    });

    this.soResults = this.rheologyService.getRheologyHierarchy(this.calculatedFluids);
    this.showGraphs = true;
  }

  printPage(): void {
    window.print();
  }

}